import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../services/apiService';
import { formatLocalDateTime } from '../../utils/dateUtils';
import { eventService, EventType } from '../../services/eventService';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Alert,
  CircularProgress,
  Tooltip,
  Snackbar,
  Stack
} from '@mui/material';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import BugReportIcon from '@mui/icons-material/BugReport';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
// Define sensor interface
interface Sensor {
  id: number;
  name: string;
  serial_number: string;
  imei: string;
  ccid: string;
  location_description: string;
  axis?: string;
  direction?: string;
  pr?: string;
  is_active: boolean;
  battery_level?: number;
  last_seen?: string;
  alert_count: number;
  last_alert_date?: string;
  suspension_status?: 'active' | 'scheduled' | null;
}

// Define response interface
interface SensorListResponse {
  items: Sensor[];
  total: number;
  page: number;
  size: number;
}

const SensorList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const [sensors, setSensors] = useState<Sensor[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(!!(location.state as any)?.snackbar);

  // Sort
  const [sortField, setSortField] = useState<string>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(0);
  };

  // Debounce searchText
  useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 400); // 400ms debounce
    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
  }, [searchText]);

  // Fetch sensors data - memoized to prevent infinite loop
  const fetchSensors = useCallback(async (page: number, limit: number, search?: string, sortField?: string, sortDir?: 'asc' | 'desc') => {
    if (!isAuthenticated) {
      setError(t('auth.loginRequired'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Use trailing slash to avoid redirect
      let url = `/sensors/?skip=${page * limit}&limit=${limit}`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      if (sortField) {
        url += `&sort_by=${encodeURIComponent(sortField)}&sort_dir=${sortDir}`;
      }

      const response = await apiClient.get<SensorListResponse>(url);
      setSensors(response.data.items);
      setTotalCount(response.data.total);
    } catch (err: any) {
      console.error('Error fetching sensors:', err);
      // Ensure error is a string, not an object
      const errorDetail = err.response?.data?.detail;
      setError(
        typeof errorDetail === 'object' ? JSON.stringify(errorDetail) : 
        errorDetail || t('common.failedToLoad', { resource: t('sensors.list').toLowerCase() })
      );
      setSensors([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, t]); // Only recreate if authentication status changes

  // Initial data load & when debouncedSearch or sort changes
  useEffect(() => {
    fetchSensors(page, rowsPerPage, debouncedSearch, sortField, sortDir);
  }, [page, rowsPerPage, debouncedSearch, sortField, sortDir, fetchSensors]);

  // Subscribe to events
  useEffect(() => {
    if (isAuthenticated) {
      // Set up event listener for new alerts
      const alertUnsubscribe = eventService.subscribe(EventType.NEW_ALERT, () => {
        // Refresh sensors list when a new alert is received
        fetchSensors(page, rowsPerPage, searchText, sortField, sortDir);
      });
      
      // Set up event listener for sensor updates
      const sensorUpdateUnsubscribe = eventService.subscribe(EventType.SENSOR_UPDATE, (sensorData) => {
        // Update sensor in the list if it exists
        setSensors(prevSensors => {
          return prevSensors.map(sensor => {
            if (sensor.id === sensorData.id) {
              return {
                ...sensor,
                last_seen: sensorData.last_seen,
                battery_level: sensorData.battery_level
              };
            }
            return sensor;
          });
        });
      });
      
      // Clean up subscriptions when component unmounts
      return () => {
        alertUnsubscribe();
        sensorUpdateUnsubscribe();
      };
    }
  }, [isAuthenticated, fetchSensors, page, rowsPerPage, searchText, sortField, sortDir]);

  // Handle page change
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    setPage(0);
  };

  // Handle click on sensor row
  const handleSensorClick = (sensorId: number) => {
    navigate(`/sensors/${sensorId}`);
  };

  // Handle add new sensor
  const handleAddSensor = () => {
    navigate('/sensors/new');
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchSensors(page, rowsPerPage, searchText, sortField, sortDir);
  };

  // Handle test mode
  const handleTestMode = (sensorId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`/test/${sensorId}`);
  };

  // Handle CSV export
  const handleExportCsv = async () => {
    try {
      // Make a direct fetch request to handle CSV download
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'}/sensors/export/csv`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/csv',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to export CSV');
      }

      // Get the CSV content as blob
      const blob = await response.blob();
      
      // Create blob link to download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with current date
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD format
      link.setAttribute('download', `sensors_export_${dateStr}.csv`);
      
      // Append to html link element page
      document.body.appendChild(link);
      
      // Start download
      link.click();
      
      // Clean up and remove the link
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setError(t('common.failedToOperation'));
    }
  };

  // Check if user is ingecom
  const isIngecomUser = user?.role === 'ingecom';

  if (loading && sensors.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen && !!location.state?.snackbar}
        autoHideDuration={6000}
        onClose={() => {
          setSnackbarOpen(false);
          // Clear snackbar state in location
          if (location.state?.snackbar) {
            navigate(location.pathname, { replace: true, state: {} });
          }
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => {
            setSnackbarOpen(false);
            if (location.state?.snackbar) {
              navigate(location.pathname, { replace: true, state: {} });
            }
          }}
          severity={location.state?.snackbar?.severity || 'success'}
          sx={{ width: '100%' }}
        >
          {location.state?.snackbar?.message}
        </Alert>
      </Snackbar>

    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{t('navigation.sensors')}</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title={t('sensors.exportCsvTooltip')}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCsv}
            >
              {t('sensors.exportCsv')}
            </Button>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddSensor}
          >
            {t('sensors.add')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              {t('common.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
          <TextField
            placeholder={t('common.search_placeholder', { resource: (t('navigation.sensors') as string).toLowerCase() }) as string}
            variant="outlined"
            size="small"
            value={searchText}
            onChange={handleSearchChange}
            sx={{ mr: 2, flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title={t('common.refresh')}>
            <IconButton onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {([
                  { field: 'name',                 label: t('sensors.name') },
                  { field: 'serial_number',         label: t('sensor.fields.serialNumber') },
                  { field: 'imei',                  label: t('common.imei') },
                  { field: 'ccid',                  label: t('common.ccid') },
                  { field: 'location_description',  label: t('common.location') },
                  { field: 'axis',                  label: t('sensor.fields.axis') },
                  { field: 'direction',             label: t('sensor.fields.direction') },
                  { field: 'pr',                    label: t('sensor.fields.pr') },
                  { field: 'is_active',             label: t('common.status') },
                  { field: 'last_seen',             label: t('common.lastSeen') },
                  { field: 'alert_count',           label: t('navigation.alerts') },
                ] as { field: string; label: string }[]).map(({ field, label }) => (
                  <TableCell key={field} sortDirection={sortField === field ? sortDir : false}>
                    <TableSortLabel
                      active={sortField === field}
                      direction={sortField === field ? sortDir : 'asc'}
                      onClick={() => handleSort(field)}
                    >
                      {label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sensors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    {loading ? t('common.loading_resources', { resource: t('navigation.sensors').toLowerCase() }) : t('common.no_resources_found', { resource: t('navigation.sensors').toLowerCase() })}
                  </TableCell>
                </TableRow>
              ) : (
                sensors.map((sensor) => (
                  <TableRow 
                    key={sensor.id}
                    hover
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                    onClick={() => handleSensorClick(sensor.id)}
                  >
                    <TableCell>{sensor.name}</TableCell>
                    <TableCell>{sensor.serial_number}</TableCell>
                    <TableCell>{sensor.imei}</TableCell>
                    <TableCell>{sensor.ccid}</TableCell>
                    <TableCell>{sensor.location_description || t('common.notSet')}</TableCell>
                    <TableCell>{sensor.axis || t('common.notSet')}</TableCell>
                    <TableCell>{sensor.direction || t('common.notSet')}</TableCell>
                    <TableCell>{sensor.pr || t('common.notSet')}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        <Chip
                          label={sensor.is_active ? t('common.active') : t('common.inactive')}
                          color={sensor.is_active ? 'success' : 'error'}
                          size="small"
                        />
                        {sensor.suspension_status === 'active' && (
                          <Chip
                            label={t('sensors.suspensionStatus.suspended')}
                            color="error"
                            size="small"
                          />
                        )}
                        {sensor.suspension_status === 'scheduled' && (
                          <Chip
                            label={t('sensors.suspensionStatus.scheduled')}
                            color="primary"
                            size="small"
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {sensor.last_seen ? formatLocalDateTime(sensor.last_seen) : t('common.never')}
                    </TableCell>
                    <TableCell>
                      {sensor.alert_count > 0 ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Chip 
                            label={sensor.alert_count} 
                            color="warning" 
                            size="small" 
                            sx={{ mr: 1 }}
                          />
                          {sensor.last_alert_date && (
                            <Typography variant="caption" color="text.secondary">
                              {t('common.last')} {formatLocalDateTime(sensor.last_alert_date)}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        t('sensor.noAlertsForSensor')
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title={t('sensors.view')}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSensorClick(sensor.id);
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {isIngecomUser && (
                          <Tooltip title={t('testMode.title')}>
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={(e) => handleTestMode(sensor.id, e)}
                            >
                              <BugReportIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('pagination.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} ${t('pagination.of')} ${count}`
          }
          getItemAriaLabel={(type) => {
            switch (type) {
              case 'first':
                return t('pagination.firstPage');
              case 'last':
                return t('pagination.lastPage');
              case 'next':
                return t('pagination.nextPage');
              case 'previous':
                return t('pagination.previousPage');
              default:
                return '';
            }
          }}
        />
      </Paper>
    </Box>
    </>
  );
};

export default SensorList;